/* eslint-disable no-empty */
import { assistantActions } from '@src/reducers/assistants';
import {
  AssistantApiError,
  AssistantFile,
  AssistantTable,
  fetchAssistantApi,
} from '@src/types/assistant';
import env from '@src/utils/env';
import { call, put, takeEvery, takeLeading } from 'redux-saga/effects';

function* fetchAssistantsTableSaga({
  payload,
}: ReturnType<typeof assistantActions.fetchAssistantsTable>) {
  try {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore

    const queryParams = new URLSearchParams(payload).toString();
    const response: {
      data: AssistantTable[];
      paging: { hasMore: boolean };
    } = yield call(
      fetchAssistantApi,
      `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants?${queryParams}`,
      { method: 'GET' }
    );

    yield put(
      assistantActions.setFetchAssistantsTableRequest({
        hasMore: response?.paging?.hasMore || false,
        data: response.data,
        status: 'success',
      })
    );
  } catch (error) {
    yield put(
      assistantActions.setFetchAssistantsTableRequest({ status: 'failure' })
    );
  } finally {
    yield put(
      assistantActions.setFetchAssistantsTableRequest({ status: 'idle' })
    );
  }
}

function* updateAssistantStatusSaga({
  payload: { assistantId, assistantStatus },
}: ReturnType<typeof assistantActions.updateAssistantStatus>) {
  const baseUrl = `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants/${assistantId}`;
  try {
    yield call(fetchAssistantApi, baseUrl, {
      method: 'PATCH',
      body: JSON.stringify({ status: assistantStatus }),
    });

    yield put(
      assistantActions.updateAssistantStatusRequest({
        assistantStatus,
        status: 'success',
      })
    );
  } catch (error) {
    yield put(
      assistantActions.updateAssistantStatusRequest({ status: 'failure' })
    );
  } finally {
    yield put(
      assistantActions.updateAssistantStatusRequest({ status: 'idle' })
    );
  }
}

function* fetchAssistantStoreSaga({
  payload: assistantId,
}: ReturnType<typeof assistantActions.fetchAssistantStore>) {
  try {
    const assistantStore: {
      id: string;
    } = yield call(
      fetchAssistantApi,
      `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/assistants/${assistantId}/vector-store`,
      { method: 'GET' }
    );

    yield put(
      assistantActions.setFetchAssistantStoreRequest({
        assistantId,
        assistantStoreId: assistantStore.id,
        status: 'success',
      })
    );
  } catch (error) {
    yield put(
      assistantActions.setFetchAssistantStoreRequest({ status: 'failure' })
    );
  } finally {
    yield put(
      assistantActions.setFetchAssistantStoreRequest({ status: 'idle' })
    );
  }
}

export function* fetchAssistantFilesSaga({
  payload: { assistantId, assistantStoreId },
}: ReturnType<typeof assistantActions.fetchAssistantFiles>) {
  try {
    const assistantFiles: AssistantFile[] = yield call(
      fetchAssistantApi,
      `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/vector-stores/${assistantStoreId}/files`,
      { method: 'GET' }
    );

    // The reducer compares this instant with the time of the recent uploads
    // it keeps (ENG-2062); reading the clock here keeps the reducer pure.
    yield put(
      assistantActions.setFetchAssistantFilesRequest({
        status: 'success',
        assistantFiles,
        assistantId,
        receivedAt: Date.now(),
      })
    );
  } catch (error) {
    yield put(
      assistantActions.setFetchAssistantFilesRequest({
        assistantId,
        status: 'failure',
      })
    );
  }
}

// The message the server sent with a failed upload, when its body carries one
// (`{ message, error, status, traceId }` on ia-assistant-service errors).
const uploadErrorMessage = (error: unknown): string | undefined => {
  if (!(error instanceof AssistantApiError)) return undefined;
  const body = error.body as { message?: unknown; error?: unknown } | null;
  const text = [body?.message, body?.error].find(
    (value): value is string => typeof value === 'string' && value.trim() !== ''
  );
  return text;
};

export function* uploadFileAssistantSaga({
  payload: { assistantStoreId, assistantFiles },
}: ReturnType<typeof assistantActions.uploadAssistantFile>) {
  // Files are sent one by one. The body of each POST is the row the server
  // already confirmed, kept so the block paints it at once instead of waiting
  // for a list that may lag behind the provider (ENG-2062). On a failure the
  // files before `uploaded.length` are on the server, the rest are not (ENG-2027).
  const uploaded: AssistantFile[] = [];
  try {
    if (!assistantStoreId) return;
    // eslint-disable-next-line no-restricted-syntax
    for (const assistantFile of assistantFiles) {
      const formData = new FormData();
      formData.append('file', assistantFile, assistantFile.name);

      const created: AssistantFile = yield call(
        fetchAssistantApi,
        `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/vector-stores/${assistantStoreId}/files`,
        {
          method: 'POST',
          body: formData,
        },
        true
      );
      // Older service builds answer the row without its name or size: the
      // browser knows both from the file it just sent
      uploaded.push(
        created && typeof created === 'object'
          ? {
              ...created,
              filename: created.filename || assistantFile.name,
              usage_bytes: created.usage_bytes || assistantFile.size,
            }
          : created
      );
    }

    yield put(
      assistantActions.setUploadAssistantFileRequest({
        assistantStoreId,
        status: 'success',
        uploadedFiles: uploaded,
        uploadedAt: Date.now(),
      })
    );
  } catch (error) {
    yield put(
      assistantActions.setUploadAssistantFileRequest({
        assistantStoreId,
        status: 'failure',
        errorMessage: uploadErrorMessage(error),
        failedFilenames: assistantFiles
          .slice(uploaded.length)
          .map(file => file.name),
      })
    );
  } finally {
    yield put(
      assistantActions.setUploadAssistantFileRequest({
        assistantStoreId,
        status: 'idle',
      })
    );
  }
}

function* deleteAssistantFileSaga({
  payload: { assistantId, assistantStoreId, assistantFileId },
}: ReturnType<typeof assistantActions.deleteAssistantFile>) {
  try {
    yield call(
      fetchAssistantApi,
      `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/vector-stores/${assistantStoreId}/files/${assistantFileId}`,
      { method: 'DELETE' }
    );

    yield put(
      assistantActions.setDeleteAssistantFileRequest({
        assistantId,
        assistantFileId,
        status: 'success',
      })
    );
  } catch (error) {
    let errorMessage: string | undefined;

    if (error instanceof AssistantApiError) {
      const body = error.body as {
        error?: string;
        message?: string;
      } | null;

      if (body?.error === 'FILE_INDEXING_IN_PROGRESS') {
        errorMessage = body.message;
      } else if (body?.error === 'FILE_PURGE_UNCONFIRMED') {
        errorMessage = 'FILE_PURGE_UNCONFIRMED';
      }
    }

    yield put(
      assistantActions.setDeleteAssistantFileRequest({
        assistantId,
        status: 'failure',
        errorMessage,
      })
    );
  } finally {
    yield put(
      assistantActions.setDeleteAssistantFileRequest({
        assistantId,
        status: 'idle',
      })
    );
  }
}

export default function* assistantSaga() {
  yield takeEvery(
    assistantActions.fetchAssistantsTable,
    fetchAssistantsTableSaga
  );

  yield takeLeading(
    assistantActions.updateAssistantStatus,
    updateAssistantStatusSaga
  );

  yield takeLeading(
    assistantActions.fetchAssistantStore,
    fetchAssistantStoreSaga
  );

  yield takeLeading(
    assistantActions.fetchAssistantFiles,
    fetchAssistantFilesSaga
  );

  yield takeLeading(
    assistantActions.uploadAssistantFile,
    uploadFileAssistantSaga
  );

  yield takeEvery(
    assistantActions.deleteAssistantFile,
    deleteAssistantFileSaga
  );
}
